// utils.js — Safe DOM helpers, sanitization, notifications for Peerly
// No innerHTML from untrusted sources. All rendering uses safe DOM APIs.

import { BASE_URL } from './config.js';

let _isMobile = null;

/**
 * Detect mobile device from user agent.
 */
export function isMobileDevice() {
  if (_isMobile === null) {
    _isMobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
  }
  return _isMobile;
}

/**
 * Detect touch device.
 */
export function isTouchDevice() {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

/**
 * Create a DOM element with safe attributes and optional children.
 * Never uses innerHTML.
 *
 * @param {string} tag
 * @param {Object} [attrs] — key/value pairs for attributes. Special keys:
 *   - className: sets element.className
 *   - textContent: sets element.textContent
 *   - style: if string, sets style.cssText; if object, merges onto style
 *   - dataset: object merged onto element.dataset
 *   - children: array of child nodes/elements
 *   - events: { eventName: handler } for addEventListener
 * @returns {HTMLElement}
 */
export function el(tag, attrs = {}) {
  const element = document.createElement(tag);
  const { className, textContent, style, dataset, children, events, ...rest } = attrs;

  if (className) element.className = className;
  if (textContent !== undefined) element.textContent = String(textContent);

  if (style) {
    if (typeof style === 'string') {
      element.style.cssText = style;
    } else if (typeof style === 'object') {
      Object.assign(element.style, style);
    }
  }

  if (dataset && typeof dataset === 'object') {
    for (const [k, v] of Object.entries(dataset)) {
      element.dataset[k] = String(v);
    }
  }

  if (events && typeof events === 'object') {
    for (const [evt, handler] of Object.entries(events)) {
      element.addEventListener(evt, handler);
    }
  }

  // Safe attribute assignment — only known safe attributes
  const SAFE_ATTRS = new Set([
    'id',
    'title',
    'role',
    'tabindex',
    'aria-label',
    'aria-hidden',
    'aria-live',
    'aria-atomic',
    'aria-modal',
    'aria-haspopup',
    'aria-expanded',
    'aria-pressed',
    'type',
    'value',
    'min',
    'max',
    'placeholder',
    'disabled',
    'href',
    'target',
    'rel',
    'download',
    'for',
    'name',
    'autocomplete',
    'src',
    'alt',
    'width',
    'height',
  ]);
  for (const [k, v] of Object.entries(rest)) {
    if (SAFE_ATTRS.has(k)) {
      element.setAttribute(k, String(v));
    }
  }

  if (Array.isArray(children)) {
    for (const child of children) {
      if (child instanceof Node) {
        element.appendChild(child);
      } else if (typeof child === 'string') {
        element.appendChild(document.createTextNode(child));
      }
    }
  }

  return element;
}

/**
 * Safe linkification: detects URLs in plain text and returns an array of DOM nodes.
 * Links are created as <a> elements with safe attributes — never via innerHTML.
 *
 * @param {string} text — plain text (already safe, no HTML).
 * @returns {DocumentFragment}
 */
export function linkify(text) {
  const fragment = document.createDocumentFragment();
  const urlRegex =
    /(\b(?:https?|ftp):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])|(\bwww\.[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gi;

  let lastIndex = 0;
  let match;

  while ((match = urlRegex.exec(text)) !== null) {
    // Text before the URL
    if (match.index > lastIndex) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
    }

    const rawUrl = match[0];
    const fullUrl = rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`;

    const link = document.createElement('a');
    link.href = fullUrl;
    link.textContent = rawUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    fragment.appendChild(link);

    lastIndex = urlRegex.lastIndex;
  }

  // Remaining text after last URL
  if (lastIndex < text.length) {
    fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
  }

  return fragment;
}

/**
 * Show a toast notification. Uses safe DOM only.
 *
 * @param {string} message — plain text message
 * @param {'info'|'success'|'error'} [type='info']
 */
export function showNotification(message, type = 'info') {
  // Limit concurrent notifications to 3 — remove oldest if exceeded
  const existing = document.body.querySelectorAll('.notification');
  if (existing.length >= 3) {
    existing[0].remove();
  }

  const notification = el('div', {
    className: `notification ${type}`,
    textContent: message,
    role: 'alert',
    'aria-live': 'polite',
  });

  document.body.appendChild(notification);

  const duration = type === 'error' ? 5000 : 3000;
  setTimeout(() => {
    notification.style.animation = 'slideOutRight 0.3s var(--ease-out)';
    setTimeout(() => notification.remove(), 300);
  }, duration);
}

/**
 * Safely clears all children from an element.
 */
export function clearChildren(element) {
  while (element.firstChild) {
    element.removeChild(element.firstChild);
  }
}

/**
 * Get the base URL for share links.
 */
export function getBaseUrl() {
  if (BASE_URL) return BASE_URL;
  return `${window.location.origin}${window.location.pathname}`;
}

/**
 * Build a session URL from a room ID.
 */
export function buildSessionUrl(roomId) {
  return `${getBaseUrl()}?session=${encodeURIComponent(roomId)}`;
}

/**
 * Copy text to clipboard with fallback.
 * @returns {Promise<boolean>}
 */
export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to fallback
  }

  // Fallback: textarea + execCommand
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    textarea.remove();
    return ok;
  } catch {
    return false;
  }
}

/**
 * Safely capture a video frame as a downloadable PNG.
 * @param {HTMLVideoElement} videoElement
 * @param {string} name — label for the file name
 */
export function captureFrame(videoElement, name) {
  if (!videoElement || !videoElement.videoWidth) {
    showNotification('No video to capture', 'error');
    return;
  }
  const canvas = document.createElement('canvas');
  canvas.width = videoElement.videoWidth;
  canvas.height = videoElement.videoHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(videoElement, 0, 0);
  const a = document.createElement('a');
  a.href = canvas.toDataURL('image/png');
  a.download = `peerly-capture-${name}-${Date.now()}.png`;
  a.click();
  showNotification('Snapshot saved!', 'success');
}
